import { logger } from "@/utils/logger";

/**
 * Cookie Utility Functions
 *
 * Provides secure cookie management for storing authentication tokens.
 *
 * SECURITY BENEFITS:
 * - Cookies with Secure flag only sent over HTTPS
 * - SameSite=Strict prevents CSRF attacks
 * - Path restriction limits cookie scope
 * - Expiration enforcement
 *
 * NOTE: These are regular cookies (not httpOnly) because they're set from JavaScript.
 * For maximum security, backend should set httpOnly cookies, but this is still
 * better than localStorage as cookies have built-in security features.
 */

export interface CookieOptions {
  /**
   * Expiration time in seconds from now
   * If not provided, cookie becomes session cookie (deleted when browser closes)
   */
  maxAge?: number;

  /**
   * Cookie path (default: '/')
   */
  path?: string;

  /**
   * Secure flag - only send over HTTPS (default: true in production)
   */
  secure?: boolean;

  /**
   * SameSite attribute for CSRF protection
   * - 'Strict': Cookie only sent for same-site requests (most secure)
   * - 'Lax': Cookie sent for top-level navigation
   * - 'None': Cookie sent for all requests (requires Secure=true)
   */
  sameSite?: "Strict" | "Lax" | "None";
}

/**
 * Set a cookie with security options
 *
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options (maxAge, path, secure, sameSite)
 *
 * @example
 * setCookie('auth_token', 'abc123', { maxAge: 3600, secure: true, sameSite: 'Strict' });
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {},
): void {
  try {
    const {
      maxAge,
      path = "/",
      secure = import.meta.env.PROD, // Secure only in production (HTTPS)
      sameSite = "Strict",
    } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (maxAge !== undefined) {
      cookieString += `; Max-Age=${maxAge}`;
    }

    cookieString += `; Path=${path}`;

    if (secure) {
      cookieString += "; Secure";
    }

    cookieString += `; SameSite=${sameSite}`;

    document.cookie = cookieString;

    logger.debug(
      `[Cookies] Set cookie: ${name} (maxAge: ${maxAge || "session"})`,
    );
  } catch (error) {
    logger.error(`[Cookies] Failed to set cookie ${name}:`, error);
    throw error;
  }
}

/**
 * Get a cookie value by name
 *
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 *
 * @example
 * const token = getCookie('auth_token');
 */
export function getCookie(name: string): string | null {
  try {
    const nameEQ = encodeURIComponent(name) + "=";
    const cookies = document.cookie.split(";");

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        const value = cookie.substring(nameEQ.length);
        return decodeURIComponent(value);
      }
    }

    return null;
  } catch (error) {
    logger.error(`[Cookies] Failed to get cookie ${name}:`, error);
    return null;
  }
}

/**
 * Delete a cookie by setting its expiration to the past
 *
 * @param name - Cookie name
 * @param path - Cookie path (must match the path used when setting)
 *
 * @example
 * deleteCookie('auth_token');
 */
export function deleteCookie(name: string, path: string = "/"): void {
  try {
    // Set expiration to past date to delete cookie
    document.cookie = `${encodeURIComponent(name)}=; Path=${path}; Max-Age=0`;
    logger.debug(`[Cookies] Deleted cookie: ${name}`);
  } catch (error) {
    logger.error(`[Cookies] Failed to delete cookie ${name}:`, error);
  }
}

/**
 * Check if a cookie exists
 *
 * @param name - Cookie name
 * @returns true if cookie exists, false otherwise
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Clear all cookies (use with caution!)
 * This will delete all cookies accessible to JavaScript
 */
export function clearAllCookies(): void {
  try {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name =
        eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      deleteCookie(name);
    }
    logger.debug("[Cookies] Cleared all cookies");
  } catch (error) {
    logger.error("[Cookies] Failed to clear all cookies:", error);
  }
}

/**
 * Parse all cookies into an object
 *
 * @returns Object with cookie names as keys and values as values
 */
export function getAllCookies(): Record<string, string> {
  try {
    const cookies: Record<string, string> = {};
    const cookieStrings = document.cookie.split(";");

    for (const cookieString of cookieStrings) {
      const [name, ...valueParts] = cookieString.split("=");
      if (name && valueParts.length > 0) {
        const trimmedName = name.trim();
        const value = valueParts.join("=").trim();
        cookies[decodeURIComponent(trimmedName)] = decodeURIComponent(value);
      }
    }

    return cookies;
  } catch (error) {
    logger.error("[Cookies] Failed to parse cookies:", error);
    return {};
  }
}
